// The race screen's rules (spec §13.5 "What the athlete sees", §13.15; M6):
// each goal with its on-track indicator and the coach's latest word on it,
// the phases to each race, and the weeks of a phase with their objectives
// and, for a week not written yet, its planned sessions. Pure, like
// timeline.js: the caller passes `today`.
//
// Where it comes from:
//   - `state.strategy.on_track`: GET /v2/strategy's latest trajectory reading
//     per goal (app/plan/trajectory.py `latest`): {goal_key, goal_date, day,
//     expected, projected, status, drift}. `goal_key` is an A race's id, or
//     "goal" for a rolling strategy;
//   - the on-track message: a coach_message with body {text, kind: "on_track",
//     status, goal} (trajectory._say), sent when the shown status changes;
//   - the phase review: a plan_update with body {text, kind: "phase_review",
//     phase, next, decision} (app/coach/head/phase_review.py `_announce`).

import { phaseOn, shownPhases, weekStartOf, addDays } from './timeline';
import { formatMinutes } from '../lib/duration';

const day = (iso) => String(iso).slice(0, 10);

export const ON_TRACK_STATUSES = ['on_track', 'ahead', 'behind'];

/** What each indicator state says. `missing`: no reading yet for this goal. */
export const ON_TRACK_META = {
  on_track: { label: 'On track', hint: 'Your fitness is heading where the plan needs it on race day', goalHint: 'Your fitness is heading where the plan expects' },
  ahead: { label: 'Ahead of plan', hint: 'Your fitness is heading above what the plan expected for race day', goalHint: 'Your fitness is heading above what the plan expects' },
  behind: { label: 'Behind plan', hint: 'Your fitness is heading below what the plan expected for race day', goalHint: 'Your fitness is heading below what the plan expects' },
  missing: { label: 'Not read yet', hint: 'Your coach checks this every night once there is training on the plan to read' },
};

/** The indicator's line: race day only for a race; a rolling plan's goal has none. */
export const onTrackHint = (goal) => {
  const meta = ON_TRACK_META[goal.state] || ON_TRACK_META.missing;
  return (!goal.race && meta.goalHint) || meta.hint;
};

/** A reading's state for the indicator: one of ON_TRACK_STATUSES, else 'missing'. */
export const indicatorState = (reading) => (reading && ON_TRACK_STATUSES.includes(reading.status) ? reading.status : 'missing');

export const isOnTrackMessage = (item) => item?.type === 'coach_message' && item.body?.kind === 'on_track';
export const isPhaseReview = (item) => item?.type === 'plan_update' && item.body?.kind === 'phase_review';

/** The coach's latest on-track message about `goalKey` (by seq), or null. */
export function latestOnTrackMessage(thread, goalKey) {
  let best = null;
  for (const item of Object.values(thread || {})) {
    if (!isOnTrackMessage(item) || String(item.body.goal) !== String(goalKey)) continue;
    if (!best || item.seq > best.seq) best = item;
  }
  return best;
}

/** The last day the committed strategy plans: its last phase's end, else its last week's. */
function strategyEnd(strategy) {
  const ends = [
    ...(strategy?.timeline || []).map((p) => day(p.end_date)),
    ...Object.values(strategy?.weeks || {}).map((w) => addDays(w.week_start, 6)),
  ];
  return ends.sort().pop() || null;
}

/**
 * The goals the race screen shows, by date: every A race ahead, and every
 * goal the trajectory has a reading for that is still ahead. With no A
 * race ahead (a rolling strategy) there is one goal, "goal", at the end of
 * the plan. Each:
 * {key, title, date, race, reading, state, message}.
 */
export function seasonGoals(state, today) {
  const strategy = state?.strategy;
  if (!strategy) return [];
  const races = Object.values(state.races || {});
  const raceBy = Object.fromEntries(races.map((r) => [String(r.id), r]));
  const readings = Object.fromEntries((strategy.on_track || []).map((r) => [String(r.goal_key), r]));
  const keys = new Set([
    ...races.filter((r) => r.priority === 'A' && day(r.date) >= day(today)).map((r) => String(r.id)),
    ...Object.keys(readings),
  ]);
  const out = [];
  const add = (key) => {
    const race = raceBy[key] || null;
    const reading = readings[key] || null;
    const date = race?.date ? day(race.date) : reading?.goal_date ? day(reading.goal_date) : key === 'goal' ? strategyEnd(strategy) : null;
    if (!date || date < day(today)) return;            // a goal behind the athlete has nothing to track
    if (!race && key !== 'goal') return;               // a reading for a race since removed
    out.push({
      key, date, race, reading,
      title: race?.title || state.goal?.label || 'Your goal',
      state: indicatorState(reading),
      message: latestOnTrackMessage(state.thread, key),
    });
  };
  keys.forEach(add);
  if (!out.length && !keys.has('goal')) add('goal');   // no A race ahead: the strategy's own end
  return out.sort((a, b) => a.date.localeCompare(b.date) || a.key.localeCompare(b.key));
}

/** The phase the screen opens on: today's, else the next one, else the last. */
export function currentPhase(phases, today) {
  if (!phases.length) return null;
  const sorted = [...phases].sort((a, b) => String(a.start_date).localeCompare(String(b.start_date)));
  return phaseOn(sorted, today) || sorted.find((p) => day(p.start_date) > day(today)) || sorted.at(-1);
}

/**
 * A phase's weeks, in order: each outline week of the committed strategy
 * with `current` (today is in it), `past`, `outline` (not written yet: its
 * planned sessions are all there is) and its planned sessions by date.
 */
export function phaseWeeks(state, phase, today) {
  if (!phase) return [];
  const thisWeek = weekStartOf(today);
  const inPhase = (w) => (w.phase_id != null ? String(w.phase_id) === String(phase.id)
    : day(w.week_start) >= day(phase.start_date) && day(w.week_start) <= day(phase.end_date));
  return Object.values(state?.strategy?.weeks || {})
    .filter(inPhase)
    .sort((a, b) => day(a.week_start).localeCompare(day(b.week_start)))
    .map((w) => ({
      ...w,
      current: day(w.week_start) === thisWeek,
      past: day(w.week_start) < thisWeek,
      outline: !w.materialized,
      sessions: [...(w.sessions || [])].sort((a, b) => day(a.date).localeCompare(day(b.date))
        || Number(Boolean(b.is_key)) - Number(Boolean(a.is_key))),
    }));
}

/** Everything the race screen draws. */
export function seasonView(state, today) {
  const shown = shownPhases(state);
  const phase = currentPhase(shown.phases, today);
  return {
    hasStrategy: Boolean(state?.strategy),
    phases: shown.phases, draft: shown.draft,
    proposed: state?.draft?.stage === 'proposed',
    goals: seasonGoals(state, today),
    races: Object.values(state?.races || {}),
    phase,
  };
}

// What a week's load intent is called on screen (LoadIntent).
export const LOAD_INTENT_LABEL = {
  build: 'Build', hold: 'Hold', deload: 'Easier week', taper: 'Taper', race: 'Race week', recover: 'Recovery',
};

/** 45 → "45 min", 90 → "1h 30m", 120 → "2h" (the one convention, lib/duration). */
export function minutesLabel(min) {
  return formatMinutes(min) || '0 min';
}

const DECISION = {
  confirm: (next) => `${next} confirmed`,
  adjust: (next) => `${next} adjusted`,
  replace: (next) => `${next} being reworked`,
};

/** The phase-review card: "Base done · Build confirmed", the coach's text, and whether a rework follows. */
export function phaseReviewView(item) {
  const b = item?.body || {};
  const done = b.phase ? `${b.phase} done` : 'Phase done';
  const next = b.next ? (DECISION[b.decision] || DECISION.confirm)(b.next) : null;
  return {
    title: [done, next].filter(Boolean).join(' · '),
    text: b.text || '',
    decision: DECISION[b.decision] ? b.decision : 'confirm',
    reworkFollows: b.decision === 'replace',
  };
}
